import { ForecastNode } from "@features/builder/graph/types"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { GraphDetails } from "@features/builder/types"
import MnemonicTable from "@features/graph/details/components/MnemonicTable"
import { CircularProgress, FormHelperText, Grid } from "@mui/material"
import {
  useGetMnemonisDataQuery,
  useGetScenarioSourceMnemonicsQuery,
} from "@store/services/graphsService"
import { useFormContext } from "react-hook-form"
import { getNormalisedGraphKey } from "@utils/graph"
import { MissingScenarioFileCta } from "@features/builder/NodeDrawer/NodeForm/MissingScenarioFileCta"
import { useEffect, useMemo, useRef } from "react"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import ForecastMnemonicField from "@components/ForecastMnemonicField"

type ForecastAccordionProps = {
  node: ForecastNode
}

const ForecastAccordion = (props: ForecastAccordionProps) => {
  const form = useFormContext<NodeFormValues>()
  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = JSON.parse(graphDocumentData.data)
  const scenarioFilePath = graphData?.ScenarioSource
  const scenarioVersion = graphData?.ScenarioVersion
  const graphKey = graphData ? getNormalisedGraphKey(graphData) : ""

  // Track previous scenarioVersion to detect actual changes
  const prevScenarioVersionRef = useRef<string | null | undefined>()

  const forecastValue = form.watch("forecast_mnemonic")

  const getMnemonicsQuery = useGetScenarioSourceMnemonicsQuery(
    {
      graphKey,
      type: "forecast",
      // Include scenarioVersion to ensure query refetches when scenario file changes
      scenarioVersion: scenarioVersion ?? "",
    },
    {
      skip: !scenarioFilePath,
    },
  )

  const mnemonics = useMemo(
    () => (scenarioFilePath ? getMnemonicsQuery.data?.map(({ mnem }) => mnem) ?? [] : []),
    [getMnemonicsQuery.data, scenarioFilePath],
  )

  const shouldFetchMnemonicValues =
    !!scenarioFilePath && !!forecastValue && mnemonics.includes(forecastValue)

  const getMnemonicValuesQuery = useGetMnemonisDataQuery(
    {
      type: "forecast",
      graphKey,
      mnemonic: forecastValue ?? "",
      startDate: graphData?.StartDate ?? "rolling",
      scenarioVersion: scenarioVersion ?? "",
    },
    {
      skip: !shouldFetchMnemonicValues,
    },
  )

  // Handle scenario file changes:
  // Only run when scenarioVersion actually changes, not on every render or node switch
  useEffect(() => {
    // On first mount, initialize the ref without running logic
    if (prevScenarioVersionRef.current === undefined) {
      prevScenarioVersionRef.current = scenarioVersion
      return
    }

    // Check if scenarioVersion actually changed
    if (prevScenarioVersionRef.current === scenarioVersion) return

    // Wait for updated mnemonics when scenario file exists
    if (scenarioFilePath && getMnemonicsQuery.isFetching) return

    getMnemonicsQuery.refetch()

    if (shouldFetchMnemonicValues) {
      getMnemonicValuesQuery.refetch()
    }

    // Update ref for next comparison
    prevScenarioVersionRef.current = scenarioVersion
  }, [
    scenarioVersion,
    scenarioFilePath,
    getMnemonicsQuery,
    getMnemonicValuesQuery,
    shouldFetchMnemonicValues,
    mnemonics,
    forecastValue,
    form,
  ])

  const { getNodeValidation } = useNodesValidation()

  const mnemonicValuesData = shouldFetchMnemonicValues ? getMnemonicValuesQuery.data ?? null : null

  const isFetching = getMnemonicValuesQuery.isFetching || getMnemonicsQuery.isFetching

  if (isFetching) {
    return (
      <NodeDrawerAccordion id="forecast" label="Forecast Data">
        <Grid item justifyContent="center" sx={{ display: "flex" }}>
          <CircularProgress />
        </Grid>
      </NodeDrawerAccordion>
    )
  }

  const errors = [getNodeValidation(props.node.id, "FORECAST_MNEMONIC")?.errors].flatMap(
    (err) => err ?? [],
  )

  return (
    <NodeDrawerAccordion id="forecast" label="Forecast Data">
      <Grid item>
        <ForecastMnemonicField
          control={form.control}
          name="forecast_mnemonic"
          label="Forecast Mnemonic"
        >
          {errors.map((err, idx) => (
            <FormHelperText key={idx} error>
              - {err.msg}
            </FormHelperText>
          ))}
        </ForecastMnemonicField>
        {!scenarioFilePath && <MissingScenarioFileCta />}
      </Grid>

      {forecastValue && scenarioFilePath && mnemonicValuesData && (
        <Grid item>
          <MnemonicTable data={mnemonicValuesData} />
        </Grid>
      )}
    </NodeDrawerAccordion>
  )
}

export default ForecastAccordion
